import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchPluginPages } from './api';
import type { PluginPage } from './types';

/**
 * Fetches plugin pages whenever the user becomes authenticated.
 * `/api/plugins/pages` requires auth (returns 401 otherwise), so a
 * pre-login fetch silently fails and pages stays empty — which breaks
 * `<PluginRoutes>` because routes are derived from the fetched list.
 * Re-fetches when auth state changes so a post-login mount registers
 * plugin routes promptly. Surfaced 2026-05-26 by dns-monitor:
 * sidebar entry appeared (menu hook mounted post-login) but the
 * route was missing, so the link redirected back to /dashboard via
 * the catch-all in App.tsx.
 */
export function usePluginPages(): {
  pages: PluginPage[];
  loading: boolean;
  error: string | null;
} {
  const { isAuthenticated } = useAuth();
  const [pages, setPages] = useState<PluginPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setPages([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPluginPages()
      .then((rows) => {
        if (!cancelled) {
          setPages(rows ?? []);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[usePluginPages] failed', e);
          setError(e?.message ?? 'failed to load plugin pages');
          setPages([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return { pages, loading, error };
}
